/**
 * GlassSelect — custom dropdown matching the glassmorphism design system.
 *
 * Native <select> option lists are OS-rendered and can't be styled.
 * This component renders a button + fixed-positioned dropdown panel
 * using the app's glass CSS variables for full visual consistency.
 *
 * Uses position:fixed to escape overflow:auto scroll containers (like tables).
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface GlassSelectOption {
  value: string;
  label: string;
}

interface GlassSelectProps {
  value: string;
  options: GlassSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** Compact mode for table cells */
  compact?: boolean;
  /** Highlight when value differs from default */
  highlight?: boolean;
}

export function GlassSelect({
  value,
  options,
  onChange,
  placeholder = '— select —',
  compact = false,
  highlight = false,
}: GlassSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  // Calculate dropdown position from trigger rect
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = Math.min(options.length * 28 + 4, 220);

    // Prefer below, flip above if not enough space
    const top =
      spaceBelow >= dropdownHeight + 4
        ? rect.bottom + 2
        : rect.top - dropdownHeight - 2;

    setPos({ top, left: rect.left, width: rect.width });
  }, [open, options.length]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        listRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Scroll selected item into view when opening
  useEffect(() => {
    if (open && listRef.current && value) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }
  }, [open, value]);

  // Close on scroll of any ancestor (dropdown position would be stale)
  // but NOT when scrolling inside the dropdown itself
  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  const selectedLabel = options.find((o) => o.value === value)?.label;

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val);
      setOpen(false);
    },
    [onChange],
  );

  const py = compact ? 'py-1' : 'py-1.5';
  const textSize = compact ? 'text-[11px]' : 'text-xs';

  return (
    <>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center justify-between w-full px-2 ${py} rounded-md ${textSize} text-left transition-colors`}
        style={{
          background: highlight
            ? 'rgba(59, 130, 246, 0.12)'
            : 'var(--glass-bg)',
          border: `1px solid ${highlight ? 'rgba(59, 130, 246, 0.25)' : 'var(--glass-border)'}`,
          color: value
            ? highlight
              ? '#93c5fd'
              : 'var(--text-secondary)'
            : 'var(--text-dim)',
          cursor: 'pointer',
          minHeight: compact ? 26 : 30,
        }}
      >
        <span className="truncate">{selectedLabel ?? placeholder}</span>
        <ChevronDown
          size={12}
          className="flex-shrink-0 ml-1 transition-transform"
          style={{
            color: 'var(--text-dim)',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
          }}
        />
      </button>

      {/* Dropdown panel — portalled to body with position:fixed */}
      {open &&
        createPortal(
          <div
            ref={listRef}
            className="rounded-lg overflow-hidden"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 9999,
              background: 'var(--modal-bg-heavy)',
              border: '1px solid var(--glass-border-bright)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-selected={isSelected}
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full text-left px-3 ${py} ${textSize} transition-colors block`}
                  style={{
                    background: isSelected
                      ? 'rgba(59, 130, 246, 0.18)'
                      : 'transparent',
                    color: isSelected ? '#93c5fd' : 'var(--text-secondary)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background =
                        'rgba(255, 255, 255, 0.06)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
